import React from "react";
import AuthUserContext from "./context";
import { withFirebase } from "../Firebase";
import ExpiryPopup from "./popup";
import moment from "moment";
import * as ROUTES from "../../constants/route";
import Helpers from "../Helpers";

const initialInactiveTime = 900;

const withAuthentication = (Component) => {
  class WithAuthentication extends React.Component {
    constructor(props) {
      super(props);
      this.state = {
        authUser: null,
        timeLeft: 1000000,
        expirationDate: -1,
        popupOpen: false,
        refreshTimer: 0,
        isTokenRetrieving: false,
        inactiveTime: 1000000,
      };
      this.helpers = new Helpers();
      this.updateInactivity = this.updateInactivity.bind(this);
    }

    updateInactivity() {
      if (!this.state.popupOpen) {
        let currentTime = moment().add(initialInactiveTime, "s").unix();
        localStorage.setItem("_sessionTime", currentTime);

        let inactiveTime = currentTime - moment().unix();
        if (this.state.inactiveTime < inactiveTime) {
          this.setState({ inactiveTime });
        }
      }
    }

    initTrackers() {
      window.addEventListener("mousemove", this.updateInactivity);
      window.addEventListener("scroll", this.updateInactivity);
      window.addEventListener("keydown", this.updateInactivity);
    }

    cleanUpTrackers() {
      localStorage.removeItem("_sessionTime");
      window.removeEventListener("mousemove", this.updateInactivity);
      window.removeEventListener("scroll", this.updateInactivity);
      window.removeEventListener("keydown", this.updateInactivity);
    }

    resetState() {
      this.setState({
        timeLeft: 1000000,
        expirationDate: -1,
        popupOpen: false,
        refreshTimer: 0,
        isTokenRetrieving: false,
        inactiveTime: 1000000,
      });
    }

    componentDidMount() {
      this.listener = this.props.firebase.auth.onAuthStateChanged((authUser) => {
        // Reload on back/forward
        if (
          performance.getEntriesByType("navigation")[0].type === "back_forward"
        ) {
          window.location.reload();
          this.clickedSignOut();
        }

        if (authUser) {
          this.setState({ authUser }, () => {
            this.initTrackers();

            const currentTime = moment().add(initialInactiveTime, "s").unix();
            localStorage.setItem("_sessionTime", currentTime);

            const dateSignIn = moment(authUser.metadata.lastSignInTime);
            if (this.state.expirationDate === -1) {
              this.setState({ expirationDate: dateSignIn.add(1, "h") });
            }

            if (!this.interval) {
              this.interval = setInterval(this.tick, 1000);
            }
          });
        } else {
          this.setState({ authUser: null });
          this.resetState();
          this.cleanUpTrackers();
          clearInterval(this.interval);
          this.interval = null;
        }
      });
    }

    componentWillUnmount() {
      if (this.listener) this.listener();
      if (this.interval) clearInterval(this.interval);
      this.interval = null;
      this.cleanUpTrackers();
    }

    clickedSignOut() {
      if (this.interval) clearInterval(this.interval);
      this.interval = null;
      this.resetState();
      this.props.firebase.doSignOut();
    }

    clickedContinue() {
      let currentTime = moment().add(initialInactiveTime, "s").unix();
      localStorage.setItem("_sessionTime", currentTime);
      this.setState({
        inactiveTime: currentTime - moment().unix(),
        popupOpen: false,
      });
    }

    tick = () => {
      const { expirationDate, isTokenRetrieving } = this.state;

      const expiration = expirationDate?.clone().valueOf() - 600000;
      let timeLeft = (expiration - Date.now()) / 1000;

      if (timeLeft <= 1 && !isTokenRetrieving) {
        this.setState({ isTokenRetrieving: true }, this.updateToken);
        return;
      }

      let sessionTime = parseInt(localStorage.getItem("_sessionTime"), 10);
      let inactiveTime = sessionTime - moment().unix();

      const popupOpen = inactiveTime < 60;

      this.setState({ timeLeft, inactiveTime, popupOpen });

      if (inactiveTime <= 0) {
        clearInterval(this.interval);
        this.interval = null;
        this.resetState();
        window.location.assign(ROUTES.SESSION_ENDED);
      }
    };

    updateToken() {
      const user = this.props.firebase.auth.currentUser;

      if (!user) return;

      user.getIdToken(true)
        .then((token) => {
          this.helpers.setCookie("token", token);
          const newExpiration = this.state.expirationDate.add(1, "h");

          this.setState({
            isTokenRetrieving: false,
            expirationDate: newExpiration,
            timeLeft: 1000000,
          });

          if (!this.interval) {
            this.interval = setInterval(this.tick, 1000);
          }
        })
        .catch((err) => {
          alert(err);
        });
    }

    render() {
      return (
        <AuthUserContext.Provider value={this.state.authUser}>
          {this.state.authUser ? (
            <ExpiryPopup
              time={this.state.inactiveTime}
              signOut={() => this.clickedSignOut.bind(this)}
              continue={() => this.clickedContinue.bind(this)}
              popupOpen={this.state.popupOpen}
            />
          ) : (
            ""
          )}
          <Component {...this.props} />
        </AuthUserContext.Provider>
      );
    }
  }

  return withFirebase(WithAuthentication);
};

export default withAuthentication;
